/**
 * Review data hook — Then vs Now over published plan-of-record versions.
 *
 * "Then" is the previous published version's plan of record; "Now" is the
 * current one. The comparison is the amendment log governance recorded when
 * the version was ratified — so what this app calls "entered / exited /
 * moved" is exactly what governance calls an amendment entry.
 *
 * Reports belong to prioritization cycles (`cycle_reports.cycle_id` references
 * `degap_cycles`), so the picker lists those, not planning cycles.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCycleReportStore,
  type AmendmentLogEntry,
  type CycleReport,
  type CycleReportHistoryRow,
} from '@/stores/prioritization/cycleReportStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import {
  describeChanges,
  reviewCycles,
  type NoChangeReason,
  type ReviewCycle,
} from './reviewLogic';

export interface ReviewData {
  loading: boolean;
  error: string | null;
  cycles: ReviewCycle[];
  cycleId: string | null;
  setCycleId: (id: string) => void;
  current: CycleReport | null;
  history: CycleReportHistoryRow[];
  /** entered / exited / moved between the previous version and the current one. */
  changes: AmendmentLogEntry[];
  /** When `changes` is empty: why (first version, no difference, or not recorded). */
  noChange: NoChangeReason | null;
  reload: () => void;
}

export function useReviewData(): ReviewData {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const fetchCurrent = useCycleReportStore((s) => s.fetchCurrentCycleReport);
  const fetchHistory = useCycleReportStore((s) => s.fetchCycleReportHistory);
  const fetchCycles = useDegapProcessStore((s) => s.fetchFromSupabase);

  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [current, setCurrent] = useState<CycleReport | null>(null);
  const [history, setHistory] = useState<CycleReportHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // Cycles to review: this workspace's prioritization cycles that have a report.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await fetchCycles();
        const store = useDegapProcessStore.getState();
        if (store.syncState.syncError) throw new Error(store.syncState.syncError);
        const all = store.cycles;
        const reports = await Promise.all(all.map((c) => fetchCurrent(c.id)));
        if (cancelled) return;
        const withReport = new Set(all.filter((_, i) => reports[i] != null).map((c) => c.id));
        const list = reviewCycles(all, withReport);
        setCycles(list);
        setCycleId((prev) => (prev && list.some((c) => c.id === prev) ? prev : (list[0]?.id ?? null)));
        if (list.length === 0) setLoading(false);
      } catch {
        if (!cancelled) {
          setError('Could not read your cycles. Try refreshing.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, nonce, fetchCycles, fetchCurrent]);

  // The selected cycle's current report + its version history.
  useEffect(() => {
    if (!cycleId) {
      setCurrent(null);
      setHistory([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([fetchCurrent(cycleId), fetchHistory(cycleId)])
      .then(([report, rows]) => {
        if (cancelled) return;
        setCurrent(report);
        setHistory(rows ?? []);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not read this cycle.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cycleId, fetchCurrent, fetchHistory, nonce]);

  /**
   * What changed between the previous version and this one, from the
   * amendment log recorded when this version was ratified. When there is no
   * log to read, `noChange` says why — we never compare a report with itself.
   */
  const { changes, noChange } = useMemo(() => {
    if (!current) return { changes: [] as AmendmentLogEntry[], noChange: null };
    return describeChanges({
      version: current.version,
      amendmentLog: current.amendmentLog,
      history,
    });
  }, [current, history]);

  return { loading, error, cycles, cycleId, setCycleId, current, history, changes, noChange, reload };
}

/** Split an amendment log into the three plain-language buckets. */
export function splitChanges(changes: AmendmentLogEntry[]) {
  return {
    entered: changes.filter((c) => c.type === 'added'),
    exited: changes.filter((c) => c.type === 'removed'),
    moved: changes.filter((c) => c.type === 'rank_change'),
  };
}
